// Searchable pick lists with keyboard navigation: terms, records (R-U13 — a relation is picked by search, not a long select) and users.

import { useMemo } from 'react';
import { canWrite, recordTitle, textKey, uuidv7, type FieldDef, type Id, avatarUrl } from '@confdeck/core-shared';
import { useAccess, useMeta, useSync } from '../sync/react.tsx';
import { Avatar, Chip } from '../ui/primitives.tsx';
import { newRecordData } from '../record/new-record.ts';
import { createRecord } from '../sync/mutations.ts';
import { usePagePipeline } from '../pipelines/page-pipeline.ts';
import { scopeField, useScope } from '../shell/scope.tsx';
import s from './fields.module.css';

import { PickList, type PickOption } from '../ui/PickList.tsx';

/** Terms of a field's taxonomy; pipelines group stages under their pipeline name. */
export function TermPicker({ field, value, onChange, onDone, pipelineId }: { field: FieldDef; value: unknown; onChange: (v: unknown) => void; onDone?: () => void; pipelineId?: Id }) {
  const meta = useMeta();
  const client = useSync();
  const tax = meta.taxonomy(field.options.taxonomyId);
  const multiple = !!field.options.multiple;
  const selected = (Array.isArray(value) ? value : value ? [value] : []) as string[];
  const options = useMemo<PickOption[]>(() => {
    if (!tax) return [];
    if (tax.style === 'pipeline') {
      const roots = meta.children(null, tax.id).filter((r) => !pipelineId || r.id === pipelineId);
      return roots.flatMap((root) => meta.children(root.id, tax.id).map((t) => ({ id: t.id, label: t.name, group: roots.length > 1 ? root.name : undefined, render: <Chip color={t.color} dot>{t.name}</Chip> })));
    }
    return meta.terms(tax.id).map((t) => ({ id: t.id, label: t.name, render: <Chip color={t.color}>{t.name}</Chip> }));
  }, [tax, meta, pipelineId, meta.all.size]);
  return (
    <PickList options={options} selected={selected} multiple={multiple} onDone={onDone}
      onPick={(id) => onChange(multiple ? (selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]) : id)}
      // New labels can be added right from the picker; pipeline stages are edited in field settings.
      onCreate={tax && tax.style === 'labels' ? (name) => {
        const id = uuidv7();
        client.mutate('term.create', { id, taxonomyId: tax.id, parentId: null, name });
        onChange(multiple ? [...selected, id] : id);
      } : undefined}
    />
  );
}

/** Records of the relation's target type, searched by title; the 50 best matches are listed. */
export function RecordPicker({ field, value, onChange, onDone, multipleOverride }: { field: FieldDef; value: Id[]; onChange: (ids: Id[]) => void; onDone?: () => void; multipleOverride?: boolean }) {
  const client = useSync();
  const meta = useMeta();
  const access = useAccess();
  const [scopeId] = useScope();
  const target = meta.relationTarget(field);
  const multiple = multipleOverride ?? !!target?.many;
  // «Создать и привязать» (stage B, ЭС16): where the person may write the target and its title is typed by people, not set by app commands.
  const tType = meta.type(target?.typeId);
  const creatable = !!tType && canWrite(access, tType.id) && !meta.field(tType.id, tType.titleField)?.options.managed;
  // A new record of a type with pipelines joins the pipeline of the page the person is on, so a deal made from a relation lands in
  // the funnel the person is in, not in the first one (MiniMax review 30.10, P2-3); newRecordData keeps it only for its own field.
  const pipeId = usePagePipeline();
  const options = (q: string): PickOption[] => {
    if (!target) return [];
    const k = textKey(q);
    const out: PickOption[] = [];
    const chosen = new Set(value);
    for (const id of value) { const r = client.store.record(id); if (r) out.push({ id, label: recordTitle(r, meta) }); }
    for (const id of client.store.recordIds(target.typeId)) {
      if (out.length >= 50) break;
      if (chosen.has(id)) continue;
      const r = client.store.record(id);
      const title = recordTitle(r, meta);
      if (!k || textKey(title).includes(k)) out.push({ id, label: title });
    }
    return out;
  };
  return (
    <PickList options={options} selected={value} multiple={multiple} onDone={onDone} placeholder={`Найти: ${meta.type(target?.typeId)?.nameMany.toLowerCase() ?? ''}`}
      onPick={(id) => onChange(multiple ? (value.includes(id) ? value.filter((x) => x !== id) : [...value, id]) : (value[0] === id ? [] : [id]))}
      onCreate={creatable ? (name) => {
        // The new record gets the chosen event and first stages, like the section's «+»; its own card is where the rest is filled in.
        const sf = scopeId ? scopeField(meta, tType!.id, client.header?.scope?.typeId) : undefined;
        // Created like the section's «+» (with its optimistic copy), so the chip shows the new record at once.
        const nid = createRecord(client, tType!.id, newRecordData(meta, tType!.id, name, { scope: sf && scopeId ? { field: sf.key, id: scopeId } : null, pipelineId: pipeId }));
        onChange(multiple ? [...value, nid] : [nid]);
      } : undefined}
      createLabel={tType ? `Создать в «${tType.nameMany}»` : undefined} />
  );
}

/** One user, or several when `onChangeMany` is given (a «several users» field). */
export function UserPicker({ value, onChange, onChangeMany, onDone }: { value: unknown; onChange?: (v: string | null) => void; onChangeMany?: (v: string[]) => void; onDone?: () => void }) {
  const meta = useMeta();
  const options = meta.users().map((u) => ({ id: u.id, label: u.name, render: <span className={s.user}><Avatar name={u.name} color={u.color} src={avatarUrl(u)} size={18} /><span>{u.name}</span></span> }));
  if (onChangeMany) {
    const list = Array.isArray(value) ? value.map(String) : value ? [String(value)] : [];
    return <PickList options={options} multiple selected={list} onDone={onDone} onPick={(id) => onChangeMany(list.includes(id) ? list.filter((x) => x !== id) : [...list, id])} />;
  }
  return <PickList options={options} selected={value ? [String(value)] : []} onDone={onDone} onPick={(id) => onChange?.(id === value ? null : id)} />;
}
